import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { ChatMessageFormat } from 'types/enums/ChatMessageFormat';
import { User } from 'types/interfaces/user/User';

import { getErrorMessageWithDefault } from 'helpers/errors';
import {
  CreditsFunnels,
  Funnels,
  getFunnelSearchString,
} from 'helpers/funnels/funnels';
import { getIsInsufficientCreditError } from 'helpers/payment/helpers';
import { assertCreditsInsufficientError } from 'helpers/utils/checkType';
import { useMatchPopupAnalytics } from 'hooks/analytics/useMatchPopupAnalytics';
import { useMedia } from 'hooks/useMedia';
import { useToast } from 'hooks/useToast';
import { useUpgradePageTransition } from 'hooks/useUpgradePageTransition';
import { getUser } from 'store/auth/selectors';
import { removeMessage } from 'store/messenger/messengerSlice';
import {
  sendTextMessage,
  sendTextMessageWithInsert,
} from 'store/messenger/thunks';
import { setCreditsAmount } from 'store/payment/paymentSlice';
import {
  setActiveSystemPopup,
  SystemPopupTypes,
} from 'store/systemPopup/systemPopupSlice';

import { ReactComponent as HeartOutlineIcon } from 'assets/icons/like-outline.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { MessageInput } from 'components/shared/Thread/submodules/MessageInput';
import { UserAvatar } from 'components/shared/UserAvatar';
import { Popup } from 'components/UI/Popup';

import css from './perfectMatchPopup.module.sass';

const SUCCESS_CREDITS_PURCHASED_EVENT_NAME = 'SUCCESS_CREDITS_PURCHASED';

type ActionsPayload = BasePopupActionPayload<'close'>;

interface DataProps {
  contact: User;
}

type Props = BasePopupProps<DataProps, (action: ActionsPayload) => void>;

export const PerfectMatchPopup: React.FC<Props> = ({ data, onAction }) => {
  const { t } = useTranslation(['common', 'app']);
  const dispatch = useDispatch();
  const history = useHistory();

  const { isMobile } = useMedia();

  const currentUser = useSelector(getUser);

  const { showErrorToast } = useToast();

  const { handleSafePushUpgradePage } = useUpgradePageTransition();

  const { trackPopupShow, trackPopupClose, trackPopupType, trackPopupSend } =
    useMatchPopupAnalytics();

  const contact = data?.contact;
  const contactId = contact?.id as number;

  const MATCH_ICEBREAKER_MESSAGE = t('common:hello');

  const handle402Response = useCallback(() => {
    ReactDOM.unstable_batchedUpdates(() => {
      const search = getFunnelSearchString(Funnels.MatchFullScreen, {
        photo: contact?.photo_url ?? '',
        username: contact?.username ?? '',
        userId: contactId,
      });

      handleSafePushUpgradePage(search);
    });
  }, [
    contact?.photo_url,
    contact?.username,
    contactId,
    handleSafePushUpgradePage,
  ]);

  const handleSendMessageError = useCallback(
    async (err: any, messageId: number) => {
      const status = err?.response?.status;
      const isCreditsInsufficientError = getIsInsufficientCreditError(
        err?.response?.data?.code
      );
      const creditsBalance =
        isCreditsInsufficientError && err?.response?.data?.credits;

      if (status === 402) {
        handle402Response();

        return;
      }

      if (assertCreditsInsufficientError(err)) {
        history.push(`/threads/${contactId}`);

        dispatch(setCreditsAmount(creditsBalance));

        if (err.response.data.refill_package_info) {
          dispatch(
            setActiveSystemPopup({
              type: SystemPopupTypes.CreditsRefill,
              params: {
                funnel: CreditsFunnels.PayMessages,
                packageId: err.response.data.refill_package_info,
                successEvent: {
                  name: SUCCESS_CREDITS_PURCHASED_EVENT_NAME,
                },
              },
            })
          );
        } else {
          dispatch(
            removeMessage({
              contactId,
              messageId,
            })
          );

          dispatch(
            setActiveSystemPopup({
              type: SystemPopupTypes.CreditsInsufficient,
              params: {
                funnel: CreditsFunnels.PayMessages,
              },
            })
          );
        }

        return;
      }

      showErrorToast({
        title: getErrorMessageWithDefault(err),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, contactId, handle402Response]
  );

  const handleCloseClick = () => {
    trackPopupClose({
      contactId,
    });
    onAction({ type: 'close' });
  };

  const handleInputFocus = () => {
    trackPopupType({
      contactId,
    });
  };

  const handleSendMessage = async (payload: {
    body: string;
    format: ChatMessageFormat.Text | ChatMessageFormat.MatchIcebreaker;
  }) => {
    if (contact?.is_popular && !currentUser?.is_buyer) {
      const search = getFunnelSearchString(CreditsFunnels.PayMessages, {
        userId: contactId,
      });

      handleSafePushUpgradePage(search);

      return;
    }

    const randomId = Math.round(Math.random() * 100000);

    try {
      await dispatch(
        sendTextMessageWithInsert({
          messageId: randomId,
          contactId,
          body: payload.body,
          format: payload.format,
        })
      );
    } catch (err) {
      handleSendMessageError(err, randomId);
    }
  };

  const handleSendtextMessageAfterPurchased = useCallback(
    (messageBody: string) => {
      dispatch(
        sendTextMessage({
          format: ChatMessageFormat.Text,
          contactId: Number(contactId),
          body: messageBody,
        })
      );
    },
    [contactId, dispatch]
  );

  const handleMatchIcebreakerClick = () => {
    handleSendMessage({
      format: ChatMessageFormat.MatchIcebreaker,
      body: MATCH_ICEBREAKER_MESSAGE,
    });
    history.push(`/threads/${contactId}`);
    onAction({ type: 'close' });
  };

  const handleSendButtonClick = (payload: any) => {
    handleSendMessage({
      format: ChatMessageFormat.Text,
      body: payload.body,
    });

    trackPopupSend({
      contactId,
    });
    history.push(`/threads/${contactId}`);
    onAction({ type: 'close' });
  };

  useEffect(() => {
    trackPopupShow({ contactId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleCreditsPurchasedSuccess = (e: CustomEvent<string>) => {
      handleSendtextMessageAfterPurchased(e.detail);
    };

    document.addEventListener(
      SUCCESS_CREDITS_PURCHASED_EVENT_NAME,
      handleCreditsPurchasedSuccess as EventListener
    );

    return () => {
      document.removeEventListener(
        SUCCESS_CREDITS_PURCHASED_EVENT_NAME,
        handleCreditsPurchasedSuccess as EventListener
      );
    };
  }, [handleSendtextMessageAfterPurchased]);

  return (
    <Popup
      isFullScreen={isMobile}
      isIndented={false}
      onClose={handleCloseClick}
    >
      <div className={css.root}>
        <p className={css.title}>
          <Trans
            i18nKey="matchPopup.title"
            ns="app"
            components={{
              span: <span className={css.subtitle} />,
            }}
          />
        </p>
        <ul className={css.avatarsList}>
          <li className={css.avatarsItem}>
            <UserAvatar
              url={contact?.photo_url}
              rounded
              size="100px"
              className={css.avatar}
              gender={contact?.gender}
            />
          </li>
          <li className={css.avatarsItem}>
            <UserAvatar
              url={currentUser?.photo_url}
              rounded
              size="100px"
              className={css.avatar}
              gender={currentUser?.gender}
            />
          </li>
        </ul>
        <HeartOutlineIcon className={css.heartIcon} />
        <p className={css.description}>
          {t('app:matchPopup.subtitle', {
            username: contact?.username,
          })}
        </p>
        <p className={css.question}>
          {t('app:matchPopup.description', {
            username: contact?.username,
          })}
        </p>
        <BaseButton
          className={css.matchIcebreaker}
          type={ButtonTypes.Accent}
          small
          onClick={handleMatchIcebreakerClick}
        >
          {t('app:matchPopup.phrase', {
            phrase: MATCH_ICEBREAKER_MESSAGE,
          })}
        </BaseButton>

        <div className={css.messageInput}>
          <MessageInput
            isStickersEnabled={false}
            isStickerInputShown={false}
            isCreditsTooltipExist={false}
            isRestoreFocus={false}
            onSend={handleSendButtonClick}
            onFocus={handleInputFocus}
            wrapperClassName={!isMobile ? css.messageWrapperDesktop : ''}
          />
        </div>
      </div>
    </Popup>
  );
};
